import {
  SearchResult,
  SearchResultType,
  BudgetSearchResultRecord,
  ProposalSearchResultRecord,
  WorksheetSearchResultRecord,
  AdjustmentLineSearchResultRecord,
  WorkforceSearchResultRecord,
  StorySearchResultRecord,
  PublicationSearchResultRecord,
  ClassicReportSearchResultRecord,
  NextGenCustomReportSearchResultRecord,
  NextGenStandardReportSearchResultRecord,
  SearchResultDisplayData,
} from "../SearchResultRow/SearchResultRow.types";

import { assertUnreachable } from "./typeUtils";
import {
  getBudgetRecordUrl,
  getProposalRecordUrl,
  getWorksheetRecordUrl,
  getAdjustmentLineRecordUrl,
  getWorkforceRecordUrl,
  getStoryRecordUrl,
  getPublicationRecordUrl,
  getClassicReportRecordUrl,
  getNextGenCustomReportRecordUrl,
  getNextGenStandardReportRecordUrl,
} from "./SearchResultsRowLinks.utils";
import { IntlShape } from "react-intl";

export const getSearchResultRecordIdKey = (record: SearchResult): string => {
  switch (record.__typename) {
    case SearchResultType.Budget: {
      const bugetIdKey: keyof BudgetSearchResultRecord = "budgetId";
      return bugetIdKey;
    }
    case SearchResultType.Proposal: {
      const proposalIdKey: keyof ProposalSearchResultRecord = "proposalId";
      return proposalIdKey;
    }
    case SearchResultType.Worksheet: {
      const worksheetIdKey: keyof WorksheetSearchResultRecord = "worksheetId";
      return worksheetIdKey;
    }
    case SearchResultType.AdjustmentLine: {
      const adjustmentLineIdKey: keyof AdjustmentLineSearchResultRecord = "displayAccountString";
      return adjustmentLineIdKey;
    }
    case SearchResultType.Workforce: {
      const workforceIdKey: keyof WorkforceSearchResultRecord = "workforceId";
      return workforceIdKey;
    }
    case SearchResultType.Story: {
      const storyIdKey: keyof StorySearchResultRecord = "storyId";
      return storyIdKey;
    }
    case SearchResultType.Publication: {
      const publicationIdKey: keyof PublicationSearchResultRecord = "publicationId";
      return publicationIdKey;
    }
    case SearchResultType.ClassicReport: {
      const classicReportIdKey: keyof ClassicReportSearchResultRecord = "classicReportId";
      return classicReportIdKey;
    }
    case SearchResultType.NextGenCustomReport: {
      const nextGenCustomReportIdKey: keyof NextGenCustomReportSearchResultRecord = "nextGenReportId";
      return nextGenCustomReportIdKey;
    }
    case SearchResultType.NextGenStandardReport: {
      const nextGenStandardReportIdKey: keyof NextGenStandardReportSearchResultRecord = "lensId";
      return nextGenStandardReportIdKey;
    }
    default:
      return "id";
  }
};

export const getRecordDisplayData = (record: SearchResult, intl: IntlShape): SearchResultDisplayData | null => {
  const searchResultTypes = {
    budget: intl.formatMessage({
      defaultMessage: "Budget",
    }),
    proposal: intl.formatMessage({
      defaultMessage: "Proposal",
    }),
    worksheet: intl.formatMessage({
      defaultMessage: "Worksheet",
    }),
    adjustmentLine: intl.formatMessage({
      defaultMessage: "Adjustment Line",
    }),
    story: intl.formatMessage({
      defaultMessage: "Story",
    }),
    publication: intl.formatMessage({
      defaultMessage: "Publication",
    }),
    classicReport: intl.formatMessage({
      defaultMessage: "Classic Report",
    }),
    nextGenStandardReport: intl.formatMessage({
      defaultMessage: "Next Gen Standard Report",
    }),
    nextGenCustomReport: intl.formatMessage({
      defaultMessage: "Next Gen Custom Report",
    }),
    workforce: intl.formatMessage({
      defaultMessage: "Workforce Plan",
    }),
  };

  try {
    switch (record.__typename) {
      case SearchResultType.Budget:
        return {
          id: record.id,
          title: record.name,
          documentType: searchResultTypes.budget,
          url: getBudgetRecordUrl(record),
          originalRecord: record,
        };
      case SearchResultType.Proposal:
        return {
          id: record.id,
          title: record.name,
          documentType: searchResultTypes.proposal,
          url: getProposalRecordUrl(record),
          originalRecord: record,
          tooltipMetadata: {
            [searchResultTypes.budget]: record.budgetName,
          },
        };
      case SearchResultType.Worksheet:
        return {
          id: record.id,
          title: record.name,
          documentType: searchResultTypes.worksheet,
          url: getWorksheetRecordUrl(record),
          originalRecord: record,
          tooltipMetadata: {
            [searchResultTypes.budget]: record.budgetName,
            [searchResultTypes.proposal]: record.proposalName,
          },
        };
      case SearchResultType.AdjustmentLine:
        return {
          id: record.id,
          title: record.displayAccountString,
          documentType: searchResultTypes.adjustmentLine,
          url: getAdjustmentLineRecordUrl(record),
          originalRecord: record,
          tooltipMetadata: {
            [searchResultTypes.budget]: record.budgetName,
            [searchResultTypes.proposal]: record.proposalName,
            [searchResultTypes.worksheet]: record.worksheetName,
          },
        };
      case SearchResultType.Workforce:
        return {
          id: record.id,
          title: record.name,
          documentType: searchResultTypes.workforce,
          url: getWorkforceRecordUrl(record),
          originalRecord: record,
        };
      case SearchResultType.Story:
        return {
          id: record.id,
          title: record.name,
          documentType: searchResultTypes.story,
          url: getStoryRecordUrl(record),
          originalRecord: record,
          tooltipMetadata: record.publicationTitle
            ? {
                [searchResultTypes.publication]: record.publicationTitle,
              }
            : undefined,
        };
      case SearchResultType.Publication:
        return {
          id: record.id,
          title: record.name,
          documentType: searchResultTypes.publication,
          url: getPublicationRecordUrl(record),
          originalRecord: record,
        };
      case SearchResultType.ClassicReport:
        return {
          id: record.id,
          title: record.name,
          documentType: searchResultTypes.classicReport,
          url: getClassicReportRecordUrl(record),
          originalRecord: record,
        };
      case SearchResultType.NextGenCustomReport:
        return {
          id: record.id,
          title: record.name,
          documentType: searchResultTypes.nextGenCustomReport,
          url: getNextGenCustomReportRecordUrl(record),
          originalRecord: record,
        };
      case SearchResultType.NextGenStandardReport:
        return {
          id: record.id,
          title: record.name,
          documentType: searchResultTypes.nextGenStandardReport,
          url: getNextGenStandardReportRecordUrl(record),
          originalRecord: record,
        };
      default:
        return assertUnreachable(record);
    }
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const getValidRecordsDisplayData = (searchResults: SearchResult[], intl: IntlShape): SearchResultDisplayData[] =>
  searchResults.reduce<SearchResultDisplayData[]>((acc, record, idx) => {
    record.id = `${idx}`;
    const displayData = getRecordDisplayData(record, intl);
    if (displayData) acc.push(displayData);
    return acc;
  }, []);
